import { ColDef, GetRowIdParams, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useState } from 'react';

import { aggridHeaderClass, SmallTitleSpan } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultTQC3020_R01BomDto, TQC3020_R01BomDto } from '../Dto_TQC3020_R01';
import { useTQC3020_R01Store } from '../Store_TQC3020_R01';

const BOM_COLUMN_STATE_KEY = 'TQC3020_R01-bom-grid-column-state';

const applyBottomCenterAlign = <T,>(defs: ColDef<T>[], bottomLabelCols: Set<string>): ColDef<T>[] =>
	defs.map((def) => {
		if (!def?.colId || !bottomLabelCols.has(def.colId)) return def;
		return {
			...def,
			cellStyle: (params: { node?: { rowPinned?: string | null } }) =>
				params?.node?.rowPinned === 'bottom' ? { textAlign: 'center' } : null,
		};
	});

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC3020_R01BomDto> | null>;
	onRowClick?: (data: TQC3020_R01BomDto) => void;
}

export const BottomTableBom_TQC3020_R01 = ({ gridRef, onRowClick }: Props) => {
	const {
		TQC3020_R01bomList,
		selectedTQC3020_R01Bom,
		setSelectedTQC3020_R01Bom,
		TQC3020_R01bomColumnState,
		setTQC3020_R01BomColumnState,
		TQC3020ScrollTop,
		setTQC3020ScrollTop,
	} = useTQC3020_R01Store();

	const [, setHoveredRowIndex] = useState<number | null>(null);

	const {
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'itemCd',
		gridData: TQC3020_R01bomList,
		defaultGridData: {} as TQC3020_R01BomDto,
		selectedRow: selectedTQC3020_R01Bom,
	});

	const getBomRowId = useCallback(
		(params: GetRowIdParams<TQC3020_R01BomDto>) =>
			JSON.stringify([params.data.itemCd, params.data.lotNo]),
		[],
	);

	const baseColumnDefs: ColDef<TQC3020_R01BomDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '투입수량', colId: 'lotQty', field: 'lotQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
		{
			headerName: 'Lot No', colId: 'lotNo', field: 'lotNo', width: 150,
			headerClass: aggridHeaderClass,
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<TQC3020_R01BomDto>[] = useMemo(() => {
		const merged = TQC3020_R01bomColumnState === null
			? baseColumnDefs
			: GridCustomColumnDefs<TQC3020_R01BomDto>({
				baseColumnDefs,
				columnState: TQC3020_R01bomColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyBottomCenterAlign(merged, new Set(['itemCd']));
	}, [TQC3020_R01bomColumnState, settingRowIndex, baseColumnDefs]);

	const pinnedBottomRowData = useMemo(() => ([{
		itemCd: `${TQC3020_R01bomList.length.toLocaleString()}건`,
		lotQty: TQC3020_R01bomList.reduce((sum, item) => sum + (item.lotQty || 0), 0).toLocaleString(),
	}]), [TQC3020_R01bomList]);

	const handleRowClick = useCallback((e: RowClickedEvent<TQC3020_R01BomDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		if (!e.data) return;
		setSelectedTQC3020_R01Bom(e.data);
		onRowClick?.(e.data);
	}, [setSelectedTQC3020_R01Bom, onRowClick]);

	return (
		<div className="h-full flex flex-col">
			<SmallTitleSpan>자재·재공품 투입(소비) 내역</SmallTitleSpan>
			<div className="flex-1 min-h-0">
				<GridContextWrapper<TQC3020_R01BomDto>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					initSelected={() => setSelectedTQC3020_R01Bom(undefined)}
					defaultRowData={defaultTQC3020_R01BomDto}
					baseColumnDefs={TQC3020_R01bomColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setTQC3020_R01BomColumnState}
					localStorageKey={BOM_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<TQC3020_R01BomDto>
						ref={gridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getBomRowId}
						rowClassRules={createRowClassRules<TQC3020_R01BomDto>({
							idKey: 'valueIndex',
							selectedList: selectedTQC3020_R01Bom?.valueIndex ? [selectedTQC3020_R01Bom.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleRowClick}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottomRowData}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={BOM_COLUMN_STATE_KEY}
						setColumnState={setTQC3020_R01BomColumnState}
						scrollTop={TQC3020ScrollTop.bom}
						setScrollTop={(top) => setTQC3020ScrollTop('bom', top)}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
